import type { RideTrackPoint } from './rides';

/** Metrics the review map line / chart traces can be coloured by. */
export type ReviewMetric = 'speed' | 'longg' | 'lean';

interface MetricSpec {
  label: string;
  unit: string;
  /** 'zero' = baseline at the bottom (speed); 'centered' = zero line in the middle (signed). */
  mode: 'zero' | 'centered';
  /** Point → display value (kph / g / deg), or null when the fix lacks it. */
  value: (p: RideTrackPoint) => number | null;
  /** Colour ramp stops over the display value — shared by the map ramp and the chart trace. */
  stops: [number, string][];
}

export const METRICS: Record<ReviewMetric, MetricSpec> = {
  speed: {
    label: 'Speed',
    unit: ' km/h',
    mode: 'zero',
    value: (p) => (p.speedMps != null ? p.speedMps * 3.6 : null),
    stops: [
      [0, '#2979ff'],
      [40, '#00c896'],
      [80, '#ffb020'],
      [120, '#ff4400'],
    ],
  },
  longg: {
    label: 'Long-G',
    unit: ' g',
    mode: 'centered',
    value: (p) => p.longG ?? null,
    // brake (−) red → accel (+) green. No grey: a faint G already shows a light tint, the
    // two tints meet right at zero, and the colour saturates to deep/solid well before the
    // extreme so hard braking / hard drive reads instantly.
    stops: [
      [-1, '#d50000'],
      [-0.3, '#ff5b2e'],
      [-0.04, '#ffd8ce'],
      [0.04, '#d0ecd2'],
      [0.3, '#43a047'],
      [1, '#1b5e20'],
    ],
  },
  lean: {
    label: 'Lean',
    unit: '°',
    mode: 'centered',
    value: (p) => p.leanDeg ?? null,
    // left (−) blue → right (+) pink. Same no-grey treatment: light tints meet at upright,
    // deep colour by full lean.
    stops: [
      [-45, '#1546c8'],
      [-15, '#4f8bff'],
      [-2, '#d4e4ff'],
      [2, '#ffd6e8'],
      [15, '#ff4d97'],
      [45, '#c2185b'],
    ],
  },
};

export const METRIC_ORDER: ReviewMetric[] = ['speed', 'longg', 'lean'];

export function metricLabel(metric: ReviewMetric): string {
  return METRICS[metric].label;
}

/**
 * maplibre `line-color` expression over each segment's `v` property. Structurally an
 * ExpressionSpecification, but typed loosely — this package can't depend on a map
 * renderer, so cast at the map call site.
 */
export function rampExpression(metric: ReviewMetric): (string | number | unknown[])[] {
  const args: (number | string)[] = [];
  for (const [v, c] of METRICS[metric].stops) args.push(v, c);
  return ['interpolate', ['linear'], ['get', 'v'], ...args];
}

/** The same ramp interpolated in JS (canvas/SVG stroke), so charts match the map exactly. */
export function colorAt(metric: ReviewMetric, value: number): string {
  const { stops } = METRICS[metric];
  if (value <= stops[0][0]) return stops[0][1];
  const last = stops[stops.length - 1];
  if (value >= last[0]) return last[1];
  for (let i = 1; i < stops.length; i++) {
    const [v1, c1] = stops[i - 1];
    const [v2, c2] = stops[i];
    if (value <= v2) return lerpHex(c1, c2, (value - v1) / (v2 - v1));
  }
  return last[1];
}

function lerpHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r},${g},${bl})`;
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
